import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { supabase, isSupabaseConfigured, testSupabaseConnection } from '../lib/supabaseClient';
import ReviewForm from './ReviewForm';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

interface Review {
  id: string;
  customer_name: string;
  rating: number;
  comment: string;
  event_type: string;
  created_at: string;
}

interface ReviewsProps {
  /** Visa formuläret direkt (t.ex. /recension) */
  initialShowForm?: boolean;
  /** Order-id från /recension?order=… */
  rentalRequestId?: string | null;
}

const Reviews: React.FC<ReviewsProps> = ({
  initialShowForm = false,
  rentalRequestId = null,
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(initialShowForm);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase är inte konfigurerad.');
      }

      const isConnected = await testSupabaseConnection();
      if (!isConnected) {
        throw new Error('Ingen kontakt med Supabase.');
      }

      const { data, error: fetchError } = await supabase
        .from('reviews')
        .select('*')
        .eq('approved', true)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setReviews(data || []);
    } catch (err: any) {
      console.error('Error fetching reviews:', err);
      setError(err.message || 'Kunde inte hämta recensioner.');
    } finally {
      setLoading(false);
    }
  };

  // Bestäm vilka recensioner som ska visas
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);
  const hasMoreReviews = reviews.length > 3;
  
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: sv });
    } catch (e) {
      return 'Nyligen';
    }
  };
  
  return (
    <section className="py-16 md:py-20 border-t border-slate-800">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Vad våra kunder säger
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-6">
            Läs vad andra tycker, eller dela med dig av dina egna erfarenheter.
          </p>
          <button 
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-none transition-colors"
          >
            <Star className="mr-2" size={18} />
            {showForm ? 'Dölj formulär' : 'Lämna en recension'}
          </button>
        </div>

      {showForm && (
        <div className="max-w-2xl mx-auto my-10">
          <ReviewForm variant="dark" rentalRequestId={rentalRequestId} />
        </div>
      )}

      {/* Visa spinner först */}
      {loading ? (
        <div className="mt-10 animate-pulse">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-slate-900/35 border border-slate-800 p-6 flex flex-col md:flex-row gap-4"
              >
                <div className="w-full md:w-[240px] space-y-3">
                  <div className="h-4 bg-slate-700 rounded w-28" />
                  <div className="h-3 bg-slate-700 rounded w-full" />
                  <div className="h-3 bg-slate-700 rounded w-4/5" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="h-4 bg-slate-700 rounded w-3/4" />
                  <div className="h-3 bg-slate-700 rounded w-full" />
                  <div className="h-3 bg-slate-700 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="mt-12 max-w-xl mx-auto bg-red-500/20 text-red-200 border border-red-400/30 px-6 py-6">
          {error}
        </div>
      ) : (
        <div className="mt-10">
          {reviews.length === 0 ? (
            <div className="text-center text-slate-400 bg-slate-900/35 border border-slate-800 py-12 px-6">
              <Star className="w-12 h-12 text-purple-400 mx-auto mb-3" />
              <p>Det finns inga recensioner ännu – bli den första att lämna en!</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {displayedReviews.map((review) => (
                  <article
                    key={review.id}
                    className="bg-slate-900/35 border border-slate-800 p-6 flex flex-col md:flex-row md:items-start gap-4"
                  >
                    <div className="w-full md:w-[240px]">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex" aria-label="Betyg">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-5 h-5 flex-shrink-0 ${
                                i < review.rating
                                  ? 'text-purple-400 fill-purple-400'
                                  : 'text-slate-600'
                              }`}
                            />
                          ))}
                        </div>
                      </div>

                      <p className="font-medium text-white">{review.customer_name}</p>
                      <p className="text-purple-400 text-sm">{review.event_type}</p>
                      <p className="text-slate-500 text-xs mt-0.5">
                        {formatDate(review.created_at)}
                      </p>
                    </div>

                    <div className="flex-1">
                      <p className="text-slate-300 italic text-sm leading-relaxed">
                        "{review.comment}"
                      </p>
                      <div className="mt-5 h-px bg-slate-800" />
                    </div>
                  </article>
                ))}
              </div>
              {hasMoreReviews && (
                <div className="text-center mt-8">
                  <button
                    onClick={() => setShowAllReviews(!showAllReviews)}
                    className="inline-flex items-center bg-slate-900/35 border border-slate-800 text-white font-medium py-3 px-6 rounded-none hover:bg-slate-900/50 transition-colors"
                  >
                    {showAllReviews ? `Visa färre recensioner` : `Se alla ${reviews.length} recensioner`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </section>
  );
};

export default Reviews;